import type { Account } from '@xinfer/api-client';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DescriptionItem,
  DescriptionList,
  PageHeader,
  SectionHeader,
  usePageTitle,
} from '@xinfer/ui';
import { CircleAlert, RefreshCw, XCircle } from 'lucide-react';

/**
 * The plan's limits, in the terms the gate actually enforces them.
 *
 * Concrete numbers are resolved by the control plane and pushed to the serving
 * path, so what is described here is what will actually happen to a request.
 */
export function Limits({ account }: { account: Account }) {
  usePageTitle('Limits');

  return (
    <>
      <PageHeader
        title="Limits"
        description="What the gate enforces on this plan, and why each cap exists."
        meta={<Badge variant="outline">{account.plan}</Badge>}
      />

      <Card>
        <CardHeader>
          <CardTitle>Enforced limits</CardTitle>
          <CardDescription>
            Each of these protects a specific resource. The concrete numbers are resolved by the
            control plane and pushed to the serving path.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DescriptionList>
            <DescriptionItem label="Concurrent requests">
              KV memory directly. This is the binding resource: the node holds a bounded number of
              decoding streams at once regardless of how fast anyone is typing.
            </DescriptionItem>
            <DescriptionItem label="Maximum context">
              The attention path in use. A prompt longer than this is refused rather than served
              slowly, because the slow path would take minutes and hold KV the whole time.
            </DescriptionItem>
            <DescriptionItem label="Concurrent long sessions">
              Capped separately from ordinary requests. One 512k-context session is worth about
              twenty ordinary ones, so a single quota covering both would misprice it.
            </DescriptionItem>
            <DescriptionItem label="Default output cap">
              Applied when a request does not set <code className="font-mono">max_tokens</code>{' '}
              itself.
            </DescriptionItem>
          </DescriptionList>
        </CardContent>
      </Card>

      <div className="mt-8">
        <SectionHeader
          title="What happens when you exceed one"
          description="Three different answers, and only one of them is worth retrying blindly."
        />

        <div className="space-y-3">
          <Alert variant="warning">
            <RefreshCw />
            <div>
              <AlertTitle>
                <code className="font-mono">429</code> — concurrency or quota
              </AlertTitle>
              <AlertDescription>
                Safe to retry with backoff. The request was refused because the account is at its
                limit right now, not because anything about it was wrong.
              </AlertDescription>
            </div>
          </Alert>

          <Alert variant="danger">
            <XCircle />
            <div>
              <AlertTitle>
                <code className="font-mono">400</code> — prompt over the context cap
              </AlertTitle>
              <AlertDescription>
                Retrying will not help. The prompt has to get shorter, or the plan has to allow a
                longer context.
              </AlertDescription>
            </div>
          </Alert>

          <Alert variant="info">
            <CircleAlert />
            <div>
              <AlertTitle>
                <code className="font-mono">503</code> — the service is at capacity
              </AlertTitle>
              <AlertDescription>
                Nothing is wrong with the request. The fleet has no room for it at this moment.
              </AlertDescription>
            </div>
          </Alert>
        </div>
      </div>
    </>
  );
}
